import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import { Button } from "../../Controls/Button";
import { HeaderText } from "../../Controls/HeaderText";
import { createApiService } from "../../shared/apiConfig";
import {
  PlateScanApi,
  ServerRequestsPlateScanResponse,
} from "../../shared/api/axios-client";
import { toast } from "react-toastify";
import { CalendarCheck, Camera, CheckCircle, Clock, ExclamationCircle, InfoCircle } from "react-bootstrap-icons";

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "environment",
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

const CROP_RATIO = 0.7;
const JPEG_QUALITY = 0.92;

export interface PlateSessionInfo {
  status: "active" | "expired" | "upcoming" | "not_found";
  /** Pre-formatted start date/time string, e.g. "Mar 31, 9:00 AM" */
  from?: string;
  /** Pre-formatted end date/time string, e.g. "Mar 31, 5:00 PM" */
  to?: string;
}

export interface PlateScannerModalProps {
  show: boolean;
  onHide: () => void;
  /** Return session info for the scanned plate, or null if not found. */
  getPlateSession?: (plate: string) => PlateSessionInfo | null;
}

type Stage = "camera" | "scanning" | "result" | "error";

export function PlateScannerModal({
  show,
  onHide,
  getPlateSession,
}: PlateScannerModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stage, setStage] = useState<Stage>("camera");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ServerRequestsPlateScanResponse | null>(null);
  const plateScanApi = createApiService(PlateScanApi);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    setErrorMessage(null);
    setStage("camera");
    setResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Camera access was denied.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Could not access camera. Please allow camera permission.");
    }
  }, [stopCamera]);

  useEffect(() => {
    if (show) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show, startCamera, stopCamera]);

  const handleClose = useCallback(() => {
    stopCamera();
    setStage("camera");
    setResult(null);
    setErrorMessage(null);
    onHide();
  }, [onHide, stopCamera]);

  const captureAndScan = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !streamRef.current) return;

    setStage("scanning");
    setErrorMessage(null);

    try {
      const w = video.videoWidth;
      const h = video.videoHeight;
      const cropW = Math.floor(w * CROP_RATIO);
      const cropH = Math.floor(h * CROP_RATIO);
      const x = Math.floor((w - cropW) / 2);
      const y = Math.floor((h - cropH) / 2);

      const canvas = document.createElement("canvas");
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not available");
      ctx.drawImage(video, x, y, cropW, cropH, 0, 0, cropW, cropH);

      const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
      const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");

      const response = await plateScanApi.scanPlate({
        serverRequestsPlateScanRequest: { image: base64, regionHint: "us" },
      });

      setResult(response.data ?? null);
      setStage("result");
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: string }).message)
          : "Scan failed. Please try again.";
      setErrorMessage(msg);
      setStage("error");
      toast.error("Plate scan failed. Please try again.");
    }
  }, [plateScanApi]);

  const handleRetry = useCallback(() => {
    setResult(null);
    setErrorMessage(null);
    startCamera();
  }, [startCamera]);

  if (!show) return null;

  const scannerBrand = "#e91e8c";
  const cornerBase: React.CSSProperties = {
    position: "absolute",
    width: 22,
    height: 22,
    borderColor: scannerBrand,
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    zIndex: 2,
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <HeaderText>{stage === "result" ? "Scan result" : "Scan plate"}</HeaderText>
      </Modal.Header>
      <Modal.Body className="p-3">
        <style>{`
          @keyframes captureRipple {
            0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0; }
            8%   { opacity: 0.18; }
            100% { transform: translate(-50%, -50%) scale(1.9); opacity: 0; }
          }
          .capture-btn-ripple::before,
          .capture-btn-ripple::after {
            content: "";
            position: absolute;
            width: 48px;
            height: 48px;
            left: 50%;
            top: 50%;
            border-radius: 50%;
            background: ${scannerBrand};
            animation: captureRipple 3s ease-out infinite;
          }
          .capture-btn-ripple::after {
            animation-delay: 1.5s;
            animation-fill-mode: backwards;
          }
          @keyframes resultFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .result-fade-in {
            animation: resultFadeIn 0.5s ease-in-out forwards;
          }
        `}</style>

        {/* ── Camera stage ── */}
        {stage === "camera" && (
          <>
            <div className="position-relative rounded-3 overflow-hidden bg-dark mb-3">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-100"
                style={{ display: "block", maxHeight: "60vh", objectFit: "cover" }}
              />
              {/* Corner brackets */}
              <div style={{ ...cornerBase, top: 10, left: 10, borderTopWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, top: 10, right: 10, borderTopWidth: 3, borderRightWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, left: 10, borderBottomWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, right: 10, borderBottomWidth: 3, borderRightWidth: 3 }} />
            </div>

            <p className="text-muted small mb-3 text-center">
              Position the license plate within the frame, then tap to capture
            </p>

            <div className="d-flex justify-content-center mt-2 mb-1">
              <button
                type="button"
                className="capture-btn-ripple border-0 bg-transparent p-0 position-relative d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48 }}
                onClick={captureAndScan}
                aria-label="Capture"
              >
                <span
                  className="position-relative rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48, background: scannerBrand, zIndex: 1 }}
                >
                  <Camera size={20} color="#fff" />
                </span>
              </button>
            </div>
          </>
        )}

        {/* ── Scanning stage ── */}
        {stage === "scanning" && (
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 260 }}>
            <p className="text-muted mb-0">Reading plate...</p>
          </div>
        )}

        {/* ── Result stage ── */}
        {stage === "result" && result && (() => {
          const plateStr = (result.plate ?? "").trim();
          const session = plateStr && getPlateSession ? getPlateSession(plateStr) : null;
          const confidencePct = result.confidence != null ? Math.round(result.confidence * 100) : null;
          const confidenceLevel = confidencePct == null ? null : confidencePct >= 90 ? "High" : confidencePct >= 75 ? "Medium" : "Low";

          const sessionPill: Record<string, { bg: string; color: string; icon: React.ReactNode; label: string }> = {
            active:    { bg: "#d1f5e0", color: "#198754", icon: <CheckCircle size={13} className="me-1" />,       label: "Active session" },
            expired:   { bg: "#fde8ee", color: "#c0392b", icon: <InfoCircle size={13} className="me-1" />,        label: "Session expired" },
            upcoming:  { bg: "#deeeff", color: "#0d6efd", icon: <CalendarCheck size={13} className="me-1" />,     label: "Upcoming session" },
            not_found: { bg: "#fff3cd", color: "#856404", icon: <ExclamationCircle size={13} className="me-1" />, label: "Not found in sessions" },
          };
          const pill = sessionPill[session?.status ?? "not_found"];

          return (
            <div className="result-fade-in">
              {/* Plate */}
              <div className="text-center mb-2 mt-4">
                <span className="fw-bold" style={{ fontSize: "2rem", letterSpacing: "0.04em" }}>
                  {result.plate ?? "—"}
                </span>
              </div>

              {/* Session status pill */}
              <div className="text-center mb-3">
                <span
                  className="d-inline-flex align-items-center px-3 py-1 rounded-pill fw-semibold"
                  style={{ background: pill.bg, color: pill.color, fontSize: "0.85rem" }}
                >
                  {pill.icon}{pill.label}
                </span>
              </div>

              {/* FROM / TO timeline */}
              {session?.from && session?.to && (
                <div className="bg-light rounded-3 px-3 py-3 mb-3">
                  <div className="d-flex align-items-center">
                    <div style={{ minWidth: 0 }}>
                      <div className="text-muted fw-semibold" style={{ fontSize: "0.62rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>From</div>
                      <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>{session.from}</div>
                    </div>
                    <div className="d-flex align-items-center mx-2" style={{ flex: 1 }}>
                      <div style={{ flex: 1, borderTop: "2px dashed #ccc" }} />
                      <Clock size={14} className="mx-1 text-muted flex-shrink-0" />
                      <div style={{ flex: 1, borderTop: "2px dashed #ccc" }} />
                    </div>
                    <div className="text-end" style={{ minWidth: 0 }}>
                      <div className="text-muted fw-semibold" style={{ fontSize: "0.62rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>To</div>
                      <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>{session.to}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Low-confidence alert */}
              {confidencePct != null && confidencePct < 75 && (
                <Alert variant="warning" className="mb-3 rounded-3 py-2 small">
                  Results may not be reliable — please review the plate manually before using.
                </Alert>
              )}

              {/* Confidence inline */}
              <div className="text-center mb-4 text-muted small">
                Confidence&nbsp;&nbsp;
                <span className="fw-bold text-dark">{confidencePct != null ? `${confidencePct}%` : "—"}</span>
                {confidenceLevel && (
                  <span className={`ms-2 fw-semibold ${confidenceLevel === "High" ? "text-success" : confidenceLevel === "Medium" ? "text-secondary" : "text-warning"}`}>
                    {confidenceLevel}
                  </span>
                )}
              </div>

              <div className="d-flex justify-content-between gap-2 flex-wrap">
                <Button type="button" className="rounded-4" onClick={handleClose}>
                  Dismiss
                </Button>
                <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                  Scan again
                </Button>
              </div>
            </div>
          );
        })()}

        {/* ── Error stage ── */}
        {stage === "error" && (
          <>
            <Alert variant="warning" className="mb-3 rounded-3">
              {errorMessage}
            </Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" className="rounded-4" onClick={handleClose}>
                Close
              </Button>
              <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </>
        )}

      </Modal.Body>
    </Modal>
  );
}
